<template>
  <!--
    The shared listing card (AppList Designer v2, W1/W2): renders a listing's appList for one entry.
    Consumed by the detail view's list and by the Design control's candidate previews, so the two
    can never drift apart. Mirrors the mobile card: a leading visual (the detail icon field, or the
    grid image as a thumbnail — ruling D8), then the rows; the system `state` row; the defaultCard
    widget with its settings; text sizes S/M/L; a monogram when the icon has no value yet (W4).
  -->
  <div class="applist-card" :class="{ 'applist-card--grid': isGrid, 'applist-card--with-lead': !!lead, 'applist-card--poster': !!poster }">
    <!-- poster tile (the Poster switch, appList.overlay): the photo fills the tile, the rows over it -->
    <div class="applist-card__poster" v-if="poster">
      <fts-file
        class="applist-card__poster-image"
        :prefixURL="`${vectorId}-${poster.fieldId}`"
        :data="poster.value"
        file-type="image"
        image-width="100%"
        image-height="100%"
        preview-disabled
      />
      <div class="applist-card__poster-scrim"></div>
    </div>
    <div class="applist-card__lead" :class="`applist-card__lead--${lead.size}`" v-if="lead">
      <fts-file
        class="applist-card__lead-image"
        :prefixURL="`${vectorId}-${lead.fieldId}`"
        :data="lead.value"
        file-type="image"
        :image-width="leadPx"
        :image-height="leadPx"
        v-if="lead.kind === 'image' && lead.value"
      />
      <div class="applist-card__lead-file" v-else-if="lead.kind === 'file' && lead.value">
        <font-awesome-icon :icon="['fas', 'paperclip']" />
      </div>
      <div
        class="applist-card__lead-selection"
        :class="{ 'applist-card__lead-selection--emoji': isEmojiLabel(lead.value) }"
        :style="{ backgroundColor: getColor(lead.optionList, lead.value) }"
        v-else-if="lead.kind === 'selection' && lead.value"
      >
        {{ selectionLabel(lead.value, lead.size) }}
      </div>
      <div class="applist-card__lead-monogram" :style="{ backgroundColor: monogram.color }" v-else>
        {{ monogram.text }}
      </div>
    </div>

    <div class="applist-card__body">
      <div class="row-wrapper" v-for="(row, rowIndex) in bodyRows" :key="rowIndex">
        <!-- defaultCard widget: the standard collaboration row -->
        <div class="default-card" v-if="isWidgetRow(row)">
          <div class="default-first-row">
            <div class="default-first-left">
              <div class="default-title designer-card-text size-m style-b" v-if="widgetSettings(row).title !== false">
                <span class="default-document-id" v-if="widgetSettings(row).documentId && item.documentId">#{{ item.documentId }}</span>
                {{ item.title || "(Untitled Empty)" }}
              </div>
            </div>
            <div class="default-first-right">
              <fts-tippy :content="item.state && item.state.name" class="list-state-wrapper"
                v-if="widgetSettings(row).state !== false && hasEntryState(item.state)">
                <fts-state :state="item.state" :states="states" :tint="!poster" class="dv-list-state" />
              </fts-tippy>
              <fts-assign-to :module-id="moduleId || vectorId" :form-data="item" :max-length="3" avatar-mode :toggle-due="false"
                :toggle-create="false" :view-mode="!assignRight" :comment-section="commentRight"
                class="dv-list-entry-assign stopPropagation" @refresh="$emit('refresh')"
                v-if="widgetSettings(row).assignees !== false && item.assignees && item.assignees.length" />
              <div class="dv-list-entry-comment" v-if="widgetSettings(row).comment !== false && item.lastComment && item.lastComment.id">
                <fts-tippy :options="{ theme: 'light-border' }" :request="{
                  dispatchName: 'loadHistory',
                  payload: { id: item.lastComment.id, type: item.lastComment.type ? item.lastComment.type : 'comment' },
                }" append-content-class="fts-collaboration-content">
                  <div slot="trigger" class="dv-list-entry-comment-trigger"
                    :class="{ 'dv-list-entry-comment-trigger--active': (item.collaborationFlags & 4) === 4 }">
                    <font-awesome-icon :icon="['far', 'comment-alt']" />
                  </div>
                  <template #content="{ content, loading }">
                    <template v-if="content">
                      <div class="collaboration-comment-audit">
                        <fts-profile :userName="content.createdBy" />
                        <div class="comment-from-now">{{ momentRelativeTime(content.createdDate) }}</div>
                      </div>
                      <div class="collaboration-comment-content" v-html="translateCommentContent(content.detail)"></div>
                      <div class="collaboration-date">{{ moment(content.createdDate).format("llll") }}</div>
                    </template>
                    <fts-spinner :size="16" show v-else-if="loading" />
                  </template>
                </fts-tippy>
                <div class="dv-list-entry-comment-count" v-if="item.commentCount">
                  {{ item.commentCount > 99 ? 99 : item.commentCount }}
                </div>
              </div>
            </div>
          </div>
          <div class="default-second-row" v-if="widgetByline(row)">
            <div class="dv-list-entry-profile">
              <fts-profile :user-name="item.audit.createdBy" :profile-photo="false"
                profile-name-class="dv-list-entry-profile-name" v-if="widgetSettings(row).createdBy !== false && item.audit && item.audit.createdBy" />
              <template v-if="widgetSettings(row).createdDate !== false && item.audit && item.audit.createdDate">
                <span v-if="widgetSettings(row).createdBy !== false && item.audit.createdBy">&nbsp;·&nbsp;</span>
                <fts-tippy :content="moment(item.audit.createdDate).format('llll')" class="dv-list-entry-profile-date">
                  <div slot="trigger">{{ momentRelativeTime(item.audit.createdDate) }}</div>
                </fts-tippy>
              </template>
              <template v-if="widgetSettings(row).due !== false && item.due">
                <span>&nbsp;·&nbsp;</span>
                <span class="applist-card__due" :class="{ 'applist-card__due--overdue': isOverdue(item.due) }">
                  {{ dueLabel(item.due) }}
                </span>
              </template>
            </div>
          </div>
        </div>

        <!-- an image row that is not the leading visual -->
        <div class="applist-card__image-row" v-else-if="isImageRow(row) && getInformationValue(row.items[0].id)">
          <fts-file class="file-upload-display" :prefixURL="`${vectorId}-${row.items[0].id}`" :data="getInformationValue(row.items[0].id)"
            :file-type="`image`" downloadable :image-width="getImageWidth(row.items[0])" :image-height="getImageHeight(row.items[0])" />
        </div>

        <div class="desginer-card-row" :class="{
          'single-cell': row.items.length === 1,
          'two-cells': row.items.length === 2,
          'three-cells': row.items.length === 3,
          'cells-actions': row.items.length > 1 && row.items.every(i => i.type === 'activity'),
        }" v-else>
          <div v-for="(cell, index) in row.items" :key="cell.id" class="desginer-card-cell"
            :class="{ 'align-right': row.items.length >= 2 && index === row.items.length - 1 && cell.type !== 'activity', 'cell-lead': row.items.length === 2 && index === 0 }">
            <!-- system state row -->
            <div v-if="cell.type === 'information' && cell.id === 'state'" style="margin:4px 4px 0">
              <fts-tippy :content="item.state && item.state.name" class="list-state-wrapper" v-if="hasEntryState(item.state)">
                <fts-state :state="item.state" :states="states" :tint="!poster" class="dv-list-state" />
              </fts-tippy>
            </div>
            <div v-else-if="cell.type === 'information' && getInformation(cell.id)">
              <template v-for="value in [getInformationValue(cell.id)]">
              <template v-if="value !== undefined && value !== null && value !== ''">
                <!-- select -->
                <div v-if="getInformation(cell.id).type === 27 && !getInformation(cell.id).reference" style="margin:0 4px">
                  <div class="fts-display-form-control display-dropdown">
                    <div class="fts-display-form-tag display-dropdown-tag" :class="getTextClass(cell)"
                      :style="{ 'background-color': getColor(getInformation(cell.id).optionList, value) }">
                      {{ value }}
                    </div>
                  </div>
                </div>
                <!-- tags -->
                <div class="tag-row-wrapper" v-else-if="getInformation(cell.id).type === 28" style="margin:0 4px">
                  <div v-for="v in value" :key="v">
                    <div class="fts-display-form-tag display-dropdown-tag" :class="getTextClass(cell)"
                      :style="{ 'background-color': getColor(getInformation(cell.id).optionList, v) }">
                      {{ v }}
                    </div>
                  </div>
                </div>
                <!-- link -->
                <div v-else-if="getInformation(cell.id).type === 33" style="margin:0 4px">
                  <a class="fts-link-primary" target="_blank" :href="linkURL(value)">
                    <div :class="getTextClass(cell)">{{ value }}</div>
                  </a>
                </div>
                <!-- check box -->
                <div v-else-if="getInformation(cell.id).type === 1" style="margin:0 4px">
                  <font-awesome-icon :icon="['fas', 'check']" :style="{ 'color': 'var(--fts-primary)' }" />
                </div>
                <!-- location -->
                <div v-else-if="getInformation(cell.id).type === 9" style="margin:0 4px">
                  <fts-tippy content="View location on Google Maps" class="contact-link-display-shortcut">
                    <a class="fts-link-primary" target="_blank" :href="`http://www.google.com/maps/place/${value.lat},${value.lng}`">
                      <div :class="getTextClass(cell)">{{ value.placeName }}</div>
                    </a>
                  </fts-tippy>
                </div>
                <!-- email -->
                <div v-else-if="getInformation(cell.id).type === 25" style="margin:0 4px">
                  <a class="fts-link-primary" target="_blank" :href="'mailto:' + value">
                    <div :class="getTextClass(cell)">{{ value }}</div>
                  </a>
                </div>
                <!-- phone number -->
                <div v-else-if="getInformation(cell.id).type === 26" style="margin:0 4px">
                  <fts-tippy content="Open in WhatsApp" class="contact-link-display-shortcut">
                    <a class="fts-link-primary" target="_blank" :href="`https://wa.me/${value.replace(/\D+/g, '')}`">
                      <div :class="getTextClass(cell)">{{ value }}</div>
                    </a>
                  </fts-tippy>
                </div>
                <!-- created by -->
                <div v-else-if="getInformation(cell.id).id === 'createdBy'" style="margin:0 4px">
                  <fts-profile :class="getTextClass(cell)" :user-name="item.audit.createdBy" :profile-photo="false" />
                </div>
                <!-- created date -->
                <div v-else-if="getInformation(cell.id).id === 'createdDate'" style="margin:0 4px">
                  <fts-tippy :content="moment(value).format('llll')" class="dv-list-entry-profile-date">
                    <div :class="getTextClass(cell)">{{ momentRelativeTime(value) }}</div>
                  </fts-tippy>
                </div>
                <!-- last change by -->
                <div v-else-if="getInformation(cell.id).id === 'lastChangeBy'" style="margin:0 4px">
                  <fts-profile :class="getTextClass(cell)" :user-name="item.audit.updatedBy" :profile-photo="false" />
                </div>
                <!-- last change date -->
                <div v-else-if="getInformation(cell.id).id === 'lastChangeDate'" style="margin:0 4px">
                  <fts-tippy :content="moment(value).format('llll')" class="dv-list-entry-profile-date">
                    <div :class="getTextClass(cell)">{{ momentRelativeTime(value) }}</div>
                  </fts-tippy>
                </div>
                <!-- modules / users: one tag per referenced entry, by name -->
                <div class="tag-row-wrapper" v-else-if="getInformation(cell.id).type === 8 || getInformation(cell.id).type === 21" style="margin:0 4px">
                  <div v-for="(name, i) in referenceNames(value)" :key="i">
                    <div class="fts-display-form-tag display-dropdown-tag" :class="getTextClass(cell)">{{ name }}</div>
                  </div>
                </div>
                <div v-else style="margin:0 4px">
                  <div :class="getTextClass(cell)">{{ displayText(value) }}</div>
                </div>
              </template>
              </template>
            </div>
            <div v-else-if="cell.type === 'activity'" style="margin:8px 0 0">
              <template v-if="getActivity(cell.id)">
                <fts-btn class="desginer-activity-btn" :class="{ 'desginer-activity-btn--own': !!activityStyle(getActivity(cell.id)) }"
                  :style="activityStyle(getActivity(cell.id))" outline text-transform-init :emoji="getActivity(cell.id).emoji"
                  :color="getActivity(cell.id).color || 'primary'" @click="selectActivity(getActivity(cell.id))">
                  <i :class="getActivity(cell.id).icon" v-if="getActivity(cell.id).icon"></i>
                  <span class="btn-activity-label designer-card-text">{{ getActivity(cell.id).name }}</span>
                </fts-btn>
              </template>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import numeral from 'numeral';
import moment from 'moment';
import commons from '../commons/commons';
import FTSState from './FTSState';
import FTSTippy from './FTSTippy';
import FTSAssignTo from './FTSAssignTo';
import FTSProfile from './FTSProfile';
import FTSFile from './file/FTSFile';
import FTSBtn from './FTSBtn';
import FTSSpinner from './FTSSpinner';
import { parseColor, stateInk } from '../commons/colors';
import { hasEntryState } from '../commons/entryState';

const IMAGE_TYPES = [10, 11];
/* Layouts that can take the poster tile, as in the app. A fixed-height tile has no place in masonry
   (natural photo heights) or detail (the icon beside the fields). */
export const POSTER_LAYOUTS = ['1column', '2column', 'grid'];
const FILE_TYPES = [12, 13];
const SELECTION_TYPE = 27;
const LEAD_PX = { xs: 36, s: 44, m: 56, l: 72 };
/* A leading emoji (with optional variation selector / ZWJ sequence), as the mobile card reads it. */
const EMOJI_LEAD = /^(\p{Extended_Pictographic}(?:\uFE0F|\u200D\p{Extended_Pictographic})*)/u;

/* Pure helpers, exported for the unit tests (the component is otherwise DOM-bound). */
export const appListCardHelpers = {
  /* The card's leading visual: the detail icon field, or (grid, ruling D8) the first image row. */
  leadOf(appList, columns, valueOf) {
    if (!appList) return { lead: null, leadRowIndex: -1 };
    const find = (id) => (columns || []).find((c) => c.id === id);
    if (appList.type === 'grid') {
      const rows = appList.rows || [];
      for (let i = 0; i < rows.length; i++) {
        const items = rows[i].items || [];
        if (items.length !== 1 || items[0].type !== 'information') continue;
        const field = find(items[0].id);
        if (field && IMAGE_TYPES.indexOf(field.type) > -1) {
          const value = valueOf(field.id);
          // Ruling 2026-09-05: a grid card's photo slot collapses when there is no photo yet — the
          // app leaves it empty too — and the row is still dropped from the body; the monogram
          // fallback is kept for detail-type icons only.
          if (!appListCardHelpers.hasValue(value)) return { lead: null, leadRowIndex: i };
          return { lead: { kind: 'image', fieldId: field.id, value, size: 'l' }, leadRowIndex: i };
        }
      }
      return { lead: null, leadRowIndex: -1 };
    }
    const icon = appList.icon;
    if (!icon || !icon.informationId) return { lead: null, leadRowIndex: -1 };
    const field = find(icon.informationId);
    if (!field) return { lead: null, leadRowIndex: -1 };
    const kind = IMAGE_TYPES.indexOf(field.type) > -1 ? 'image'
      : FILE_TYPES.indexOf(field.type) > -1 ? 'file'
      : field.type === SELECTION_TYPE ? 'selection' : 'text';
    const size = (icon.size || 'm').toLowerCase();
    return {
      lead: { kind, fieldId: field.id, value: valueOf(field.id), optionList: field.optionList, size: LEAD_PX[size] ? size : 'm' },
      leadRowIndex: -1,
    };
  },
  /* The poster tile: the listing designer's Poster switch (appList.overlay). The card's first photo
     row fills the tile and the other rows sit over it on a scrim, in their designed order. It
     re-arranges the rows, so only a card that asks for it gets one, on a layout that can take it,
     and only while the photo has a value — an empty photo leaves the card as designed. */
  posterOf(appList, columns, valueOf) {
    if (!appList || appList.overlay !== true) return null;
    if (POSTER_LAYOUTS.indexOf(appList.type || '1column') < 0) return null;
    const rows = appList.rows || [];
    for (let i = 0; i < rows.length; i++) {
      const items = rows[i].items || [];
      if (items.length !== 1 || items[0].type !== 'information') continue;
      const field = (columns || []).find((c) => c.id === items[0].id);
      if (!field || IMAGE_TYPES.indexOf(field.type) < 0) continue;
      const value = valueOf(field.id);
      return appListCardHelpers.hasValue(value) ? { fieldId: field.id, value, rowIndex: i } : null;
    }
    return null;
  },
  /* The names in a reference value: the [{ id, name }] rows of a Modules (8) or Users (21) field,
     or a lone { id, name }. SS06200: without this the card printed the rows as raw JSON. */
  referenceNames(value) {
    if (value === null || value === undefined) return [];
    return (Array.isArray(value) ? value : [value])
      .map((x) => (x !== null && typeof x === 'object' ? (x.name || x.value || x.username || '') : x))
      .filter((x) => x !== '' && x !== null && x !== undefined);
  },
  /* What a plain cell shows: the value itself, or the names when it is structured. */
  displayText(value) {
    if (value === null || typeof value !== 'object') return value;
    return appListCardHelpers.referenceNames(value).join(', ');
  },
  /* An activity's own colour on its ghost button, as the app draws it: the hairline in the colour,
     the label in a readable shade of it (stateInk, per theme) and a tint of it on hover. With no
     colour of its own the button stays the neutral ghost. */
  activityStyle(color) {
    const rgb = parseColor(color);
    const ink = rgb && stateInk(color);
    if (!ink) return null;
    return {
      '--act-color': `rgb(${rgb.join(', ')})`,
      '--act-tint': `rgba(${rgb.join(', ')}, 0.08)`,
      '--act-ink': ink.light,
      '--act-ink-dark': ink.dark,
    };
  },
  /* Whether a field value is worth showing: not null, empty string or empty list. */
  hasValue(value) {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value)) return value.length > 0;
    return true;
  },
  /* Initials on a hue hashed from the entry id — what an empty icon slot shows instead of grey. */
  monogramOf(title, id) {
    const words = String(title || '').trim().split(/\s+/).filter(Boolean);
    const text = words.length === 0 ? '·'
      : words.length === 1 ? words[0].slice(0, 2).toUpperCase()
      : (words[0][0] + words[1][0]).toUpperCase();
    // Entry ids are sequential, so hashing them lands neighbours next to each other on the hue
    // wheel and a whole list comes out the same blue. Step the wheel by the golden angle instead:
    // consecutive ids are ~137° apart and any run of neighbours stays well separated.
    const key = String(id || title || '');
    let n = 0;
    if (/^\d+$/.test(key)) n = Number(key) % 100000;
    else for (let i = 0; i < key.length; i++) n = (n * 31 + key.charCodeAt(i)) >>> 0;
    const hue = Math.round((n * 137.508) % 360);
    return { text, color: `hsl(${hue}, 34%, 50%)` };
  },
  /* What a Selection lead shows: an emoji the option starts with; two initials on the small tiles
     (the option colour carries the meaning); the label itself, wrapped, on M and L. */
  /* True when the option name starts with an emoji — the tile then shows that emoji at icon size. */
  isEmojiLabel(value) {
    return EMOJI_LEAD.test(String(value == null ? '' : value).trim());
  },
  selectionLabel(value, size) {
    const text = String(value == null ? '' : value).trim();
    if (!text) return '';
    const emoji = text.match(EMOJI_LEAD);
    if (emoji) return emoji[1];
    if (size === 'xs' || size === 's') {
      const words = text.split(/[\s/_-]+/).filter(Boolean);
      return (words.length > 1 ? words[0][0] + words[1][0] : text.slice(0, 2)).toUpperCase();
    }
    return text;
  },
  textClass(cell) {
    const size = (cell.size || 'm').toLowerCase();
    const style = (cell.style || 'n').toLowerCase();
    return ['designer-card-text', `size-${size}`, `style-${style}`];
  },
  widgetSettings(row) {
    const item = row && row.items && row.items[0];
    return (item && item.settings) || {};
  },
};

export default {
  name: 'FTSAppListCard',
  components: {
    'fts-tippy': FTSTippy,
    'fts-state': FTSState,
    'fts-assign-to': FTSAssignTo,
    'fts-profile': FTSProfile,
    'fts-file': FTSFile,
    'fts-btn': FTSBtn,
    'fts-spinner': FTSSpinner,
  },
  props: {
    item: Object,
    appList: Object,
    columns: Array,
    states: Array,
    activities: Array,
    vectorId: String,
    moduleId: String,
    assignRight: { type: Boolean, default: false },
    commentRight: { type: Boolean, default: false },
  },
  computed: {
    isGrid() {
      return !!(this.appList && this.appList.type === 'grid');
    },
    poster() {
      return appListCardHelpers.posterOf(this.appList, this.columns, (id) => this.getInformationValue(id));
    },
    leadInfo() {
      return appListCardHelpers.leadOf(this.appList, this.columns, (id) => this.getInformationValue(id));
    },
    lead() {
      // A poster's photo is the tile itself, never also a thumbnail beside the rows.
      return this.poster ? null : this.leadInfo.lead;
    },
    leadPx() {
      return LEAD_PX[(this.lead && this.lead.size) || 'm'];
    },
    bodyRows() {
      const rows = (this.appList && this.appList.rows) || [];
      const skip = this.poster ? this.poster.rowIndex : this.leadInfo.leadRowIndex;
      return skip < 0 ? rows : rows.filter((_, i) => i !== skip);
    },
    monogram() {
      return appListCardHelpers.monogramOf(this.item && this.item.title, this.item && this.item.id);
    },
  },
  methods: {
    moment,
    hasEntryState,
    isWidgetRow(row) {
      return !!(row && row.items && row.items[0] && row.items[0].type === 'widget');
    },
    isImageRow(row) {
      if (!row || !row.items || row.items.length !== 1 || row.items[0].type !== 'information') return false;
      const field = this.getInformation(row.items[0].id);
      return !!field && IMAGE_TYPES.indexOf(field.type) > -1;
    },
    widgetSettings(row) {
      return appListCardHelpers.widgetSettings(row);
    },
    widgetByline(row) {
      const s = this.widgetSettings(row);
      const audit = this.item && this.item.audit;
      return (s.createdBy !== false && audit && audit.createdBy)
        || (s.createdDate !== false && audit && audit.createdDate)
        || (s.due !== false && this.item && this.item.due);
    },
    isOverdue(due) {
      return moment(due).isBefore(moment(), 'day');
    },
    dueLabel(due) {
      const days = moment(due).startOf('day').diff(moment().startOf('day'), 'days');
      if (days < 0) return `Overdue by ${-days} day${days === -1 ? '' : 's'}`;
      if (days === 0) return 'Due today';
      if (days === 1) return 'Due tomorrow';
      return `Due ${moment(due).format('ll')}`;
    },
    getImageSize(item) {
      const ratio = (item.ratio || '4:3').trim();
      const orientation = (item.orientation || 'landscape').toLowerCase();
      const [w, h] = ratio.split(':').map(Number);
      if (!w || !h) return { width: 300, height: 300 };
      if (orientation === 'portrait') return { width: 300, height: Math.round((300 * w) / h) };
      return { width: 300, height: Math.round((300 * h) / w) };
    },
    getImageWidth(item) {
      return this.getImageSize(item).width;
    },
    getImageHeight(item) {
      return this.getImageSize(item).height;
    },
    selectionLabel(value, size) {
      return appListCardHelpers.selectionLabel(value, size);
    },
    isEmojiLabel(value) {
      return appListCardHelpers.isEmojiLabel(value);
    },
    getTextClass(cell) {
      return appListCardHelpers.textClass(cell);
    },
    activityStyle(activity) {
      // Shortlist and Mark Delivered read as the secondary action (a blue ghost button) when they have no colour of their own.
      const BLUE_ACTIVITIES = ['Shortlist', 'Mark Delivered'];
      const color = activity && (activity.color || (BLUE_ACTIVITIES.indexOf(activity.name) > -1 ? '#2b9ec6' : null));
      return appListCardHelpers.activityStyle(color);
    },
    linkURL(value) {
      return String(value).toLowerCase().startsWith('http') ? value : `//${value}`;
    },
    momentRelativeTime(date) {
      return commons.momentRelativeTime(date);
    },
    translateCommentContent(content) {
      return commons.translateCommentContent(content);
    },
    getInformation(informationId) {
      return (this.columns || []).find((information) => information.id == informationId);
    },
    getActivity(activityId) {
      return (this.activities || []).find((activity) => activity.id == activityId);
    },
    getInformationValue(informationId) {
      const item = this.item || {};
      if (informationId === 'documentId') return item.documentId;
      if (informationId === 'createdBy') return item.audit && item.audit.createdBy;
      if (informationId === 'lastChangeBy') return item.audit && item.audit.updatedBy;
      if (informationId === 'createdDate') return item.audit && item.audit.createdDate;
      if (informationId === 'lastChangeDate') return item.audit && item.audit.updatedDate;

      const information = this.getInformation(informationId);
      if (!information || !item.extension) return undefined;
      const value = item.extension[information.fieldName];
      if (value === null || value === undefined) return undefined;

      if (information.type === 2) {
        return value.toLocaleString(undefined);
      } else if (information.type === 3) {
        if (information.customFormat) return numeral(value).format(information.customFormat);
        if (information.displayFormat) return numeral(value).format(information.displayFormat);
        const decimalPlaces = commons.isInteger(information.decimalPlaces) ? information.decimalPlaces : 2;
        return value.toLocaleString(undefined, { minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces });
      } else if (information.type === 4) {
        return numeral(value).format('0,0.00');
      } else if (information.type === 5 || information.type === 6) {
        const dateFormat = (format) => luxon.fromISO(value).toFormat(commons.dateFormatTranslate(format));
        if (information.enableCustomDateFormat && information.customDateFormatId) return dateFormat(information.customDateFormatId);
        if (information.predefinedFormatId) return dateFormat(information.predefinedFormatId);
        return dateFormat(information.type === 5 ? 'DD' : 'DD t');
      } else if (information.type === 9 && typeof value === 'object') {
        return {
          lat: value.lat !== undefined ? value.lat : value.latitude,
          lng: value.lng !== undefined ? value.lng : value.longitude,
          placeName: value.placeName,
        };
      }
      return value;
    },
    referenceNames(value) {
      return appListCardHelpers.referenceNames(value);
    },
    displayText(value) {
      return appListCardHelpers.displayText(value);
    },
    getColor(optionList, text) {
      return commons.selectOptionBackground({ optionList, text });
    },
    selectActivity(activity) {
      if (typeof event !== 'undefined' && event && event.stopPropagation) event.stopPropagation();
      this.$emit('activity:selected', activity);
    },
  },
};
</script>

<style scoped>
.applist-card {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 12px;
  align-items: flex-start;
}

.applist-card__body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
}

/* leading visual: detail icon or grid thumbnail */
.applist-card__lead {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 12px;
  background: var(--fts-gray-100);
  color: var(--fts-dark);
}

.applist-card__lead--xs { width: 36px; height: 36px; border-radius: 8px; font-size: 0.75rem; }
.applist-card__lead--s  { width: 44px; height: 44px; border-radius: 8px; font-size: 0.8125rem; }
.applist-card__lead--m  { width: 56px; height: 56px; font-size: 0.9375rem; }
.applist-card__lead--l  { width: 72px; height: 72px; font-size: 1.125rem; }

.applist-card__lead-image { display: block; }

.applist-card__lead-file {
  color: var(--fts-default);
  font-size: 1.125rem;
}

.applist-card__lead-selection {
  width: 100%;
  height: 100%;
  padding: 0 3px;
  box-sizing: border-box;
  overflow: hidden;
  text-align: center;
  line-height: 1.15;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 4px;
  text-align: center;
  line-height: 1.15;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: inherit;
}

.applist-card__lead-selection--emoji { font-size: 1.375rem; line-height: 1; padding: 0; }
.applist-card__lead--xs .applist-card__lead-selection--emoji { font-size: 1.125rem; }
.applist-card__lead--m .applist-card__lead-selection--emoji { font-size: 1.75rem; }
.applist-card__lead--l .applist-card__lead-selection--emoji { font-size: 2.25rem; }
.applist-card__lead-monogram {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: inherit;
}

.row-wrapper {
  display: flex;
  flex-direction: column;
}

.applist-card__image-row {
  margin: 4px;
}

/* Poster tile (appList.overlay): the photo fills the card, a scrim darkens its lower half and the
   rows sit on it in white — the app's 155px tile and three-stop scrim, on the web's 8px radius. */
.applist-card--poster {
  position: relative;
  min-height: 155px;
  border-radius: 8px;
  overflow: hidden;
  align-items: flex-end;
}
.applist-card__poster {
  position: absolute;
  inset: 0;
}
.applist-card__poster-image {
  width: 100%;
  height: 100%;
}
.applist-card__poster-scrim {
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.95) 0%, rgba(0, 0, 0, 0.6) 50%, rgba(0, 0, 0, 0) 80%);
}
.applist-card--poster .applist-card__body {
  position: relative;
  padding: 8px;
}
.applist-card--poster .designer-card-text,
.applist-card--poster .default-first-row,
.applist-card--poster .default-second-row {
  color: #fff;
}
.applist-card--poster .designer-card-text.style-l,
.applist-card--poster .default-document-id {
  color: rgba(255, 255, 255, 0.8);
}
/* A select/tag pill (its own pastel background) keeps its dark label instead of the poster's
   white text, which washed out to near-invisible on a light fill. */
.applist-card--poster .display-dropdown-tag {
  color: var(--fts-dark);
}

.default-card {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.default-first-row {
  justify-content: space-between;
  color: var(--fts-text);
  display: flex;
}

.default-second-row {
  display: flex;
  justify-content: start;
  color: var(--inistate-text-secondary, var(--fts-default));
  font-size: 0.75rem;
  line-height: 16px;
}

.default-first-left {
  display: flex;
  overflow: hidden;
}

.default-first-right {
  display: flex;
  gap: 4px;
  align-items: center;
  margin-top: -4px;
}

.default-document-id {
  color: var(--inistate-text-secondary, var(--fts-default));
  font-weight: 400;
  margin-right: 4px;
}

.dv-list-entry-profile {
  display: flex;
  align-items: center;
}

.applist-card__due--overdue {
  color: var(--fts-danger, #d50000);
  font-weight: 500;
}

.desginer-card-row {
  display: flex;
}

.desginer-card-row.single-cell { justify-content: flex-start; }
.desginer-card-row.two-cells { justify-content: space-between; }
.desginer-card-row.three-cells { justify-content: flex-start; }
/* A row of action buttons (Shortlist/Reject and the like) sits together, not spread to the row's edges. */
.desginer-card-row.cells-actions { justify-content: flex-start; gap: 6px; padding: 0 4px; }

.desginer-card-cell {
  display: inline-block;
  min-width: 0;
}

.desginer-card-cell.align-right {
  margin-left: auto;
}

.display-dropdown-tag {
  padding: 0 6px;
  border-radius: 4px;
  font-size: 0.75rem;
  line-height: 18px;
  background-color: var(--fts-gray-100);
  color: var(--fts-dark);
}

.tag-row-wrapper {
  display: flex;
  gap: 4px;
}

/* W2: a real S/M/L scale (S was 0.5rem = unreadable, M had no rule at all) */
/* Calm density (2026-09-05, same vocabulary as the Lite listing pass): 13px ledger base on 20px
   lines, S/L a step either side, emphasis by weight (600) and de-emphasis by colour, never by hue. */
.designer-card-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--fts-text);
}
.designer-card-text.size-s { font-size: 0.75rem; line-height: 16px; }
.designer-card-text.size-m { font-size: 0.8125rem; line-height: 20px; }
.designer-card-text.size-l { font-size: 0.9375rem; line-height: 22px; }
.designer-card-text.style-l { font-weight: 400; color: var(--inistate-text-secondary, var(--fts-default)); }
.designer-card-text.style-n { font-weight: 400; }
.designer-card-text.style-b { font-weight: 600; }

.desginer-card-row.two-cells .desginer-card-cell.cell-lead { flex: 0 0 auto; max-width: 68%; }
/* The trailing cell hugs its value so a tinted chip is the width of its label, not of the
   leftover row - `margin-left: auto` above is what puts it flush right. It still keeps the
   whole remainder available (basis = content, shrink 1), so a long value only truncates when
   it genuinely does not fit (F4). */
.desginer-card-row.two-cells .desginer-card-cell.align-right { flex: 0 1 auto; }
.desginer-activity-btn {
  max-width: 100%;
}
.applist-card .desginer-activity-btn.fts-btn {
  height: 28px;
  min-height: 28px;
  padding: 0 10px;
  font-size: 0.8125rem;
  font-weight: 500;
  background-color: var(--fts-card-background) !important;
  border-color: var(--fts-gray-200) !important;
  color: var(--fts-default) !important;
  transition: color 0.15s ease, background-color 0.15s ease, border-color 0.15s ease;
  &:hover {
    background-color: var(--inistate-color-neutral-50, var(--fts-gray-50)) !important;
    border-color: var(--fts-gray-300) !important;
    color: var(--fts-text) !important;
  }
  .btn-activity-label { color: inherit; font-size: inherit; }
}
/* An activity with a colour of its own keeps the ghost form but wears its colour: hairline and
   label in the colour (the label walked to AA by stateInk), a tint of it on hover. */
.applist-card .desginer-activity-btn--own.fts-btn {
  border-color: var(--act-color) !important;
  color: var(--act-ink) !important;
  &:hover {
    background-color: var(--act-tint) !important;
    border-color: var(--act-color) !important;
    color: var(--act-ink) !important;
  }
}
body.dark .applist-card .desginer-activity-btn--own.fts-btn {
  color: var(--act-ink-dark) !important;
}
/* On a poster the labels read white over the scrim; an own colour stays on the hairline. */
.applist-card--poster .desginer-activity-btn.fts-btn {
  border-color: rgba(255, 255, 255, 0.6) !important;
  color: #fff !important;
  &:hover {
    background-color: rgba(255, 255, 255, 0.12) !important;
    color: #fff !important;
  }
}
.applist-card--poster .desginer-activity-btn--own.fts-btn {
  border-color: var(--act-color) !important;
  color: #fff !important;
  &:hover {
    border-color: var(--act-color) !important;
    color: #fff !important;
  }
}
</style>
